import React, { useEffect, useState } from "react";
import { Download, Building2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";
const tooltipStyle = { contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 }, labelStyle: { color: "#00ff90" } };

function fmt(n) { return n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—"; }

const CHALLANS = [
  { key: "pf-ecr",    label: "PF ECR File",         desc: "Electronic Challan cum Return for EPFO",   fmt: "txt"  },
  { key: "esic",      label: "ESIC Challan",         desc: "Monthly ESIC contribution file",           fmt: "txt"  },
  { key: "pt",        label: "PT Challan",           desc: "Professional Tax challan by state",        fmt: "csv"  },
  { key: "form-24q",  label: "Form 24Q",             desc: "Quarterly TDS return for salaries",        fmt: "txt"  },
  { key: "tds-cert",  label: "TDS Certificates",     desc: "Form 16 / 16A for all employees",          fmt: "zip"  },
];

export default function StatutoryTab({ month }) {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [generating, setGenerating] = useState(null);
  const [genMsg,     setGenMsg]     = useState(null);
  const [rateForm,   setRateForm]   = useState({});
  const [savingRates,setSavingRates]= useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API}/api/admin/payroll/statutory?month=${month}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setData(d); setRateForm(d?.rates || {}); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [month]);

  const generate = async (key, fmt) => {
    setGenerating(key); setGenMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/payroll/statutory/generate/${key}?month=${month}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `${key}_${month}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      setGenMsg(`✓ ${key} generated`);
    } catch (e) { setGenMsg(`⚠ ${e.message}`); }
    finally { setGenerating(null); }
  };

  const saveRates = async () => {
    setSavingRates(true);
    try {
      await fetch(`${API}/api/admin/payroll/statutory/rates`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(rateForm),
      });
      setGenMsg("✓ Rates saved.");
    } catch (e) { setGenMsg(`⚠ ${e.message}`); }
    finally { setSavingRates(false); }
  };

  const summary = data?.summary || {};
  const chartData = [
    { label: "PF (Emp)",  value: summary.pf_employee  || 0, color: "#00ff90" },
    { label: "PF (Emp.)", value: summary.pf_employer  || 0, color: "#34d399" },
    { label: "ESIC (Emp)",value: summary.esic_employee|| 0, color: "#60a5fa" },
    { label: "ESIC (Em.)",value: summary.esic_employer|| 0, color: "#818cf8" },
    { label: "PT",        value: summary.pt           || 0, color: "#fbbf24" },
    { label: "TDS",       value: summary.tds          || 0, color: "#f87171" },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-5">

      {/* Summary */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Building2 size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Statutory Summary — {month}</span>
        </div>
        <div className="p-5 space-y-4">
          {loading ? <div className="h-32 rounded-lg bg-white/5 animate-pulse" /> : (
            <>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {[
                  ["PF (Emp)",   summary.pf_employee,   "#00ff90"],
                  ["PF (Emp.)",  summary.pf_employer,   "#34d399"],
                  ["ESIC (Emp)", summary.esic_employee, "#60a5fa"],
                  ["ESIC (Em.)", summary.esic_employer, "#818cf8"],
                  ["PT",         summary.pt,            "#fbbf24"],
                  ["TDS",        summary.tds,           "#f87171"],
                ].map(([label, value, color]) => (
                  <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
                    <p className="text-sm font-bold" style={{ color }}>{fmt(value)}</p>
                    <p className="text-xs text-white/30 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
              {chartData.length > 0 && (
                <ResponsiveContainer width="100%" height={100}>
                  <BarChart data={chartData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                    <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} width={40} />
                    <Tooltip {...tooltipStyle} formatter={v => [fmt(v), "Amount"]} />
                    <Bar dataKey="value" radius={[3,3,0,0]}>
                      {chartData.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.8} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </>
          )}
        </div>
      </div>

      {/* Challan generation */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Generate Challans & Returns</span>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {CHALLANS.map(({ key, label, desc, fmt: fileFmt }) => (
              <button key={key} onClick={() => generate(key, fileFmt)} disabled={generating !== null}
                className="text-left rounded-lg border border-white/5 bg-white/3 p-4 hover:border-[#00ff90]/20 hover:bg-[#00ff90]/5 disabled:opacity-40 transition-all group">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white/60 font-medium group-hover:text-white/80 transition-colors">{label}</span>
                  {generating === key ? <span className="text-xs text-[#00ff90]/50">…</span> : <Download size={12} className="text-white/20 group-hover:text-[#00ff90]/50 transition-colors" />}
                </div>
                <p className="text-xs text-white/25">{desc}</p>
              </button>
            ))}
          </div>
          {genMsg && <p className={`text-xs ${genMsg.startsWith("⚠") ? "text-red-400" : "text-[#00ff90]"}`}>{genMsg}</p>}
        </div>
      </div>

      {/* Statutory rates config */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Statutory Rates Config</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: "pf_employee_pct",   label: "PF Employee %",    default: 12   },
              { key: "pf_employer_pct",   label: "PF Employer %",    default: 12   },
              { key: "esic_employee_pct", label: "ESIC Employee %",  default: 0.75 },
              { key: "esic_employer_pct", label: "ESIC Employer %",  default: 3.25 },
              { key: "pf_wage_ceiling",   label: "PF Wage Ceiling ₹",default: 15000},
              { key: "esic_wage_ceiling", label: "ESIC Wage Ceiling ₹",default:21000},
              { key: "gratuity_pct",      label: "Gratuity % (Basic)",default: 4.81 },
              { key: "pt_state",          label: "PT State",          default: "KA", type: "text" },
            ].map(({ key, label, default: def, type }) => (
              <div key={key}>
                <label className={labelCls}>{label}</label>
                <input type={type || "number"} step="0.01" className={inputCls}
                  value={rateForm[key] ?? def}
                  onChange={e => setRateForm(r => ({ ...r, [key]: type === "text" ? e.target.value : parseFloat(e.target.value) }))} />
              </div>
            ))}
          </div>
          <button onClick={saveRates} disabled={savingRates}
            className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
            {savingRates ? "Saving…" : "Save Rates"}
          </button>
        </div>
      </div>
    </div>
  );
}